import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useToast } from '../hooks/use-toast';
import { ArrowLeft, Save, Plus, Trash2, GripVertical, ChevronUp, ChevronDown, Upload, FileText } from 'lucide-react';

const AdminCMSEveningMenu = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [form, setForm] = useState({
    title: 'La Carte du Soir',
    subtitle: 'À la carte',
    starters: [{ name: '', description: '', price: '', order: 0 }],
    mains: [{ name: '', description: '', price: '', order: 0 }],
    desserts: [{ name: '', description: '', price: '', order: 0 }],
    pdf_url: null,
    show_pdf: true,
    is_published: false,
    show_on_website: true
  });

  const getAuthHeader = () => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin');
      return null;
    }
    return { 'Authorization': `Basic ${auth}` };
  };

  useEffect(() => {
    fetchMenu();
  }, []);

  const fetchMenu = async () => {
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cms/evening-menu`, { headers });
      if (response.ok) {
        const data = await response.json();
        if (data) {
          setForm(prev => ({ 
            ...prev, 
            ...data,
            starters: data.starters?.length ? data.starters : [{ name: '', description: '', price: '', order: 0 }],
            mains: data.mains?.length ? data.mains : [{ name: '', description: '', price: '', order: 0 }],
            desserts: data.desserts?.length ? data.desserts : [{ name: '', description: '', price: '', order: 0 }]
          }));
        }
      }
    } catch (error) {
      console.log('No existing menu, starting fresh');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const headers = getAuthHeader();
    if (!headers) return;

    setSaving(true);
    try {
      const checkRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cms/evening-menu`, { headers });
      const existing = await checkRes.json();
      
      const method = existing ? 'PUT' : 'POST';
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cms/evening-menu`, {
        method,
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      if (response.ok) {
        toast({ title: "Succès", description: "Carte du Soir enregistrée" });
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de sauvegarder", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const headers = getAuthHeader();
    if (!headers) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cms/evening-menu/pdf`, {
        method: 'POST',
        headers: { 'Authorization': headers['Authorization'] },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setForm(prev => ({ ...prev, pdf_url: data.pdf_url }));
        toast({ title: "Succès", description: "PDF téléchargé" });
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Échec du téléchargement", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const addDish = (section) => {
    const maxOrder = Math.max(...form[section].map(d => d.order), -1);
    setForm(prev => ({
      ...prev,
      [section]: [...prev[section], { name: '', description: '', price: '', order: maxOrder + 1 }]
    }));
  };

  const updateDish = (section, index, field, value) => {
    setForm(prev => ({
      ...prev,
      [section]: prev[section].map((dish, i) => 
        i === index ? { ...dish, [field]: value } : dish
      )
    }));
  };

  const removeDish = (section, index) => {
    if (form[section].length <= 1) return;
    setForm(prev => ({
      ...prev,
      [section]: prev[section].filter((_, i) => i !== index)
    }));
  };

  const moveDish = (section, index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= form[section].length) return;
    
    setForm(prev => {
      const dishes = [...prev[section]];
      const temp = dishes[index];
      dishes[index] = dishes[newIndex];
      dishes[newIndex] = temp;
      // Update order values
      dishes.forEach((d, i) => d.order = i);
      return { ...prev, [section]: dishes };
    });
  };

  const DishList = ({ title, section, dishes }) => (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-bold text-gray-900">{title}</h2>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => addDish(section)}
          className="flex items-center gap-1"
        >
          <Plus size={16} />
          Ajouter un plat
        </Button>
      </div>
      <div className="space-y-3">
        {dishes.map((dish, index) => (
          <div key={index} className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
            {/* Order controls */}
            <div className="flex flex-col gap-1 pt-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => moveDish(section, index, 'up')}
                disabled={index === 0}
                className="h-6 w-6 p-0"
              >
                <ChevronUp size={14} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => moveDish(section, index, 'down')}
                disabled={index === dishes.length - 1}
                className="h-6 w-6 p-0"
              >
                <ChevronDown size={14} />
              </Button>
            </div>
            
            {/* Dish fields */}
            <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-2">
              <div className="md:col-span-4">
                <Input
                  value={dish.name}
                  onChange={(e) => updateDish(section, index, 'name', e.target.value)}
                  placeholder="Nom du plat"
                />
              </div>
              <div className="md:col-span-5">
                <Input
                  value={dish.description}
                  onChange={(e) => updateDish(section, index, 'description', e.target.value)}
                  placeholder="Description"
                />
              </div>
              <div className="md:col-span-2">
                <Input
                  value={dish.price}
                  onChange={(e) => updateDish(section, index, 'price', e.target.value)}
                  placeholder="Prix (ex: 18€)"
                />
              </div>
              <div className="md:col-span-1 flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeDish(section, index)}
                  className="text-red-500 hover:text-red-700"
                  disabled={dishes.length <= 1}
                >
                  <Trash2 size={18} />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <Link to="/admin/cms" className="text-gray-500 hover:text-gray-700">
              <ArrowLeft size={24} />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Carte du Soir</h1>
              <p className="text-sm text-gray-500">Menu à la carte avec prix par plat</p>
            </div>
          </div>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
          >
            <Save size={18} />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Status Controls */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Statut du menu</h2>
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center space-x-3">
              <Switch
                checked={form.is_published}
                onCheckedChange={(v) => updateField('is_published', v)}
              />
              <div>
                <Label className="font-medium">
                  {form.is_published ? 'Publié' : 'Brouillon'}
                </Label>
                <p className="text-xs text-gray-500">
                  {form.is_published ? 'Visible sur le site' : 'Non visible'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Switch
                checked={form.show_on_website}
                onCheckedChange={(v) => updateField('show_on_website', v)}
              />
              <div>
                <Label className="font-medium">Afficher sur le site</Label>
                <p className="text-xs text-gray-500">Section visible dans "Le Soir"</p>
              </div>
            </div>
          </div>
        </div>

        {/* Basic Info */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Informations générales</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Titre</Label>
              <Input
                value={form.title}
                onChange={(e) => updateField('title', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Sous-titre</Label>
              <Input
                value={form.subtitle}
                onChange={(e) => updateField('subtitle', e.target.value)}
                placeholder="À la carte"
                className="mt-1"
              />
            </div>
          </div>
        </div>

        {/* Dishes */}
        <DishList title="Entrées" section="starters" dishes={form.starters} />
        <DishList title="Plats" section="mains" dishes={form.mains} />
        <DishList title="Desserts / Gourmandises" section="desserts" dishes={form.desserts} />

        {/* PDF Upload */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Document PDF</h2>
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Switch
                checked={form.show_pdf}
                onCheckedChange={(v) => updateField('show_pdf', v)}
              />
              <Label>Afficher le lien PDF sur le site</Label>
            </div>
            
            <div className="flex items-center gap-4">
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handlePdfUpload}
                  className="hidden"
                />
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
                  <Upload size={18} />
                  <span>{uploading ? 'Téléchargement...' : 'Télécharger un PDF'}</span>
                </div>
              </label>
              
              {form.pdf_url && (
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}${form.pdf_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
                >
                  <FileText size={18} />
                  Voir le PDF actuel
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Help */}
        <div className="bg-blue-50 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-800">
            <strong>Astuce :</strong> Utilisez les flèches ↑↓ pour réorganiser l'ordre des plats. 
            L'ordre sera respecté sur le site.
          </p>
        </div>

        {/* Save Button (mobile) */}
        <div className="md:hidden">
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="w-full bg-green-600 hover:bg-green-700 flex items-center justify-center gap-2"
          >
            <Save size={18} />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </main>
    </div>
  );
};

export default AdminCMSEveningMenu;
